import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { ROUTES, COLORS, customToast } from '@common'
import { ErrorTrackingAPI, membershipServices } from '@services'
import {
    requestPasswordResetSchema,
    checkPasswordResetLimit,
    recordPasswordResetRateLimit,
    calculatePasswordResetTimeLeft,
    formatPasswordResetTimeLeft,
    isRateLimitError,
} from '@utils'

const RequestPasswordResetPage = () => {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [timeLeft, setTimeLeft] = useState(0)
    const [canRequest, setCanRequest] = useState(true)
    const timerRef = useRef(null)
    const emailRef = useRef('')

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: yupResolver(requestPasswordResetSchema),
        defaultValues: {
            email: '',
        },
    })

    const email = watch('email')

    // Check rate limit on email change
    useEffect(() => {
        if (email) {
            emailRef.current = email.toLowerCase().trim()
            const limit = checkPasswordResetLimit(emailRef.current)
            setCanRequest(limit.canRequest)
            setTimeLeft(calculatePasswordResetTimeLeft(limit))
        }
    }, [email])

    // Start countdown timer if rate limited
    useEffect(() => {
        // Clear any existing timer first
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }

        if (timeLeft > 0) {
            timerRef.current = setInterval(() => {
                const limit = checkPasswordResetLimit(emailRef.current)
                const remaining = calculatePasswordResetTimeLeft(limit)
                setTimeLeft(remaining)
                setCanRequest(remaining === 0)
            }, 1000)
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
    }, [timeLeft])

    const onSubmit = async data => {
        setIsLoading(true)

        try {
            const result = await membershipServices.requestPasswordReset({
                email: data.email.toLowerCase().trim(),
            })

            if (result.success) {
                // Success: show message (even if email doesn't exist, for security)
                customToast.success('If the email exists, a password reset link has been sent to your inbox.', { duration: 5000 })
                // Clear any existing rate limit on success
                // Note: We don't clear here because backend might still have rate limit
                // Only clear when user successfully receives email
            } else {
                // Check if it's a rate limit error
                if (isRateLimitError(result.error)) {
                    // Use backend nextAvailableTime if available, otherwise use default 1 hour
                    const normalizedEmail = data.email.toLowerCase().trim()
                    recordPasswordResetRateLimit(normalizedEmail, result.nextAvailableTime)
                    const limit = checkPasswordResetLimit(normalizedEmail)
                    setCanRequest(false)
                    setTimeLeft(calculatePasswordResetTimeLeft(limit))

                    customToast.error('Too many requests. Please wait before requesting again.', { duration: 5000 })
                } else {
                    // Other errors: still show success message for security (avoid email enumeration)
                    customToast.success('If the email exists, a password reset link has been sent to your inbox.', {
                        duration: 5000,
                    })
                }
            }
        } catch (error) {
            ErrorTrackingAPI.sendError(error, 'RequestPasswordResetPage', 'handleSubmit')
            customToast.error('An error occurred. Please try again.', { duration: 3000 })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="membership-auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <div className="auth-icon"></div>
                    <h2>Reset Password</h2>
                    <p>Enter your email address and we'll send you a link to reset your password.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: '2rem' }}>
                    <div className="form-group">
                        <label htmlFor="email">
                            <i className="bx bx-envelope"></i> Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            {...register('email')}
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            placeholder="Enter your email address"
                            autoComplete="email"
                            disabled={isLoading || !canRequest}
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                    </div>

                    {/* Rate limit countdown */}
                    {!canRequest && timeLeft > 0 && (
                        <div
                            className="alert alert-warning"
                            style={{
                                marginTop: '1rem',
                                padding: '12px 16px',
                                backgroundColor: COLORS.WARNING?.background || '#fff3cd',
                                border: `1px solid ${COLORS.WARNING?.border || '#ffc107'}`,
                                borderRadius: '8px',
                                color: COLORS.WARNING?.text || '#856404',
                                fontSize: '14px',
                            }}
                        >
                            <i className="bx bx-time"></i> Please wait {formatPasswordResetTimeLeft(timeLeft)} before requesting
                            again
                        </div>
                    )}

                    <button type="submit" className="btn-submit" disabled={isLoading || !canRequest}>
                        {isLoading ?
                            <>
                                <i className="bx bx-loader-alt bx-spin"></i> Sending...
                            </>
                        :   'Send Reset Link'}
                    </button>
                </form>

                <div className="back-link" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <button
                        onClick={() => navigate(ROUTES.Membership.auth)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: COLORS.INFO?.text || '#0d6efd',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px',
                        }}
                    >
                        ← Back to Sign In
                    </button>
                </div>
            </div>
        </div>
    )
}

export default RequestPasswordResetPage
